import React, { useState, useEffect } from "react";
import { Toaster } from "react-hot-toast";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { fetchAllowedMenus, fetchLeadGroupData } from "../Services/APIService";
import CreateGroupCard from "../Components/CreateLeadGroup";
import ViewGroupsCard from "../Components/LeadGroupView";
import UpdateGroupCard from "../Components/UpdateLeadGroup";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import { useLocation } from "react-router-dom";
import OverlayLoader from "../Components/OverlayLoader";

interface MenuItem {
  menuID: number;
  menuName: string;
  menuUrl: string;
  icon: string;
  subMenuList?: [];
}

interface GroupData {
  groupId: number;
  groupName: string;
  groupPurpose: string;
  isActive: boolean;
  tenantId: number;
}

const Groups: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [allowedMenus, setAllowedMenus] = useState<MenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateCard, setShowCreateCard] = useState(false);
  const [showUpdateCard, setShowUpdateCard] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<GroupData | null>(null);
  const [groups, setGroups] = useState<GroupData[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const location = useLocation();

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const toggleCreateForm = () => {
    setShowCreateCard(!showCreateCard);
    if (showUpdateCard) {
      setShowUpdateCard(false);
      setSelectedGroup(null);
    }
  };

  const handleEditGroup = (group: GroupData) => {
    setSelectedGroup(group);
    setShowUpdateCard(true);
    if (showCreateCard) {
      setShowCreateCard(false);
    }
    setTimeout(() => {
      const updateCard = document.getElementById("updateGroupCard");
      if (updateCard) {
        updateCard.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const handleCreateComplete = () => {
    setShowCreateCard(false);
    refreshGroups();
  };

  const handleCreateCancel = () => {
    setShowCreateCard(false);
  };

  const handleUpdateComplete = () => {
    setShowUpdateCard(false);
    setSelectedGroup(null);
    refreshGroups();
  };

  const handleUpdateCancel = () => {
    setShowUpdateCard(false);
    setSelectedGroup(null);
  };

  const refreshGroups = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getMenus();
  }, []);

  useEffect(() => {
    const getGroups = async () => {
      try {
        const fetchedGroups = await fetchLeadGroupData();
        setGroups(fetchedGroups);
      } catch (error) {
        console.error("Error fetching groups:", error);
      }
    };

    getGroups();
  }, [refreshTrigger]);

  return (
    <div className="wrapper">
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div>
          <Toaster
            position="top-center"
            reverseOrder={false}
            containerStyle={{
              zIndex: 10000,
            }}
          />
        </div>
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-12 col-lg-12">
                  {showCreateCard && (
                    <CreateGroupCard
                      onCreateComplete={handleCreateComplete}
                      onCancel={handleCreateCancel}
                    />
                  )}
                  {showUpdateCard && (
                    <div id="updateGroupCard">
                      <UpdateGroupCard
                        groupData={selectedGroup}
                        onUpdateComplete={handleUpdateComplete}
                        onCancel={handleUpdateCancel}
                      />
                    </div>
                  )}
                </div>
                <div className="col-12 col-lg-12">
                  <ViewGroupsCard
                    groupsData={groups}
                    onEditGroup={handleEditGroup}
                    onAddClick={toggleCreateForm}
                    showAddButton={!showCreateCard && !showUpdateCard}
                  />
                </div>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>
      <OverlayLoader
        loading={isLoading}
        message="Loading Lead Group Data"
        submessage="This might take a moment..."
      />
    </div>
  );
};

export default Groups;
